import { gql, useQuery } from '@apollo/client';
import { Button, HStack, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { dinero } from 'dinero.js';
import { useRouter } from 'next/router';
import * as queryString from 'query-string';
import * as React from 'react';

import {
  membership_checkout_started,
  membership_plan_selected,
} from '@/analytics';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CurrencyDisplay } from '@/components/Currency';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { useFeatureFlags } from '@/imports/ui/hooks/useFeatureFlags';
import { isGreaterThanZero } from '@/utilities';

import {
  MembershipsPageSubmitFooter_MembershipType,
  MembershipsPageSubmitFooter_MembershipTypeVariables,
} from './graphql-types/MembershipsPageSubmitFooter_MembershipType';
import { MembershipsPageSubmitFooter_currentUser } from './graphql-types/MembershipsPageSubmitFooter_currentUser';

import { PrePurchaseConfirmationModal } from './components/PrePurchaseConfirmationModal';

const GET_MEMBERSHIP = gql`
  query MembershipsPageSubmitFooter_MembershipType(
    $input: MembershipTypeInput!
  ) {
    membershipType(input: $input) {
      ... on MembershipTypeSuccess {
        membershipType {
          _id
          name
          priceV2 {
            annual
            monthly
          }
          validityMonths
          allowRenewals
        }
      }
    }
  }
`;

const CURRENT_USER = gql`
  query MembershipsPageSubmitFooter_currentUser {
    currentUser {
      _id
      membershipData {
        currentMembershipTerm {
          _id
        }
      }
      ...PrePurchaseConfirmationModal_UserFragment
    }
  }
  ${PrePurchaseConfirmationModal.userFragment}
`;

export const MembershipsPageSubmitFooter: React.FC<{
  selectedMembershipTypeId: string | undefined;
  isCurrentUserMembership: boolean;
}> = ({ selectedMembershipTypeId, isCurrentUserMembership }) => {
  const { currency } = useDefaultCurrency();
  const router = useRouter();
  const duplicateMembershipModal = useDisclosure();
  const prePurchaseConfirmationModal = useDisclosure();
  const { isMembershipV2Enabled } = useFeatureFlags();

  const isFutureMembership = router.query.isFutureMembership === 'true';

  const { data: membershipData, loading: isMembershipLoading } = useQuery<
    MembershipsPageSubmitFooter_MembershipType,
    MembershipsPageSubmitFooter_MembershipTypeVariables
  >(GET_MEMBERSHIP, {
    variables: { input: { id: selectedMembershipTypeId ?? '' } },
    skip: !selectedMembershipTypeId,
  });

  const { data: userData, loading: isUserDataLoading } =
    useQuery<MembershipsPageSubmitFooter_currentUser>(CURRENT_USER);

  const membershipTypeName =
    membershipData?.membershipType.membershipType.name ??
    'Select a membership type';

  const annualPrice = membershipData?.membershipType.membershipType.priceV2
    .annual
    ? dinero(membershipData.membershipType.membershipType.priceV2.annual)
    : dinero({ amount: 0, currency });
  const monthlyPrice = membershipData?.membershipType.membershipType.priceV2
    .monthly
    ? dinero(membershipData?.membershipType.membershipType.priceV2.monthly)
    : dinero({ amount: 0, currency });

  const doesCurrentUserHaveIndividualMembership =
    !!userData?.currentUser?.membershipData?.currentMembershipTerm;

  const getMembershipCheckoutPath = (membershipTypeId: string) =>
    isFutureMembership
      ? `/new-membership/${membershipTypeId}?${queryString.stringify({
          isFutureMembership: true,
        })}`
      : `/new-membership/${membershipTypeId}`;

  const redirectToCheckout = (membershipTypeId: string) => {
    const membershipCheckoutPath = getMembershipCheckoutPath(membershipTypeId);
    membership_checkout_started({
      entity_id: membershipTypeId,
      entity_type: 'membership_plan',
      membership_type_id: membershipTypeId,
      membership_plan_name: membershipTypeName,
      payment_item_type: 'membership',
      page_type: 'membership',
      page_section: 'membership',
    });

    if (!userData?.currentUser?._id) {
      return router.push(
        `/login?${queryString.stringify({
          isFromMembershipPage: true,
          redirect: membershipCheckoutPath,
        })}`
      );
    }
    return router.push(membershipCheckoutPath);
  };

  const handleSubmit = () => {
    if (!selectedMembershipTypeId) {
      throw new Error('Unexpected Missing Membership Type ID');
    }

    if (isCurrentUserMembership) {
      duplicateMembershipModal.onOpen();
    } else if (doesCurrentUserHaveIndividualMembership) {
      membership_plan_selected({
        entity_id: selectedMembershipTypeId,
        entity_type: 'membership_plan',
        membership_type_id: selectedMembershipTypeId,
        membership_plan_name: membershipTypeName,
        payment_item_type: 'membership',
        membership_change_type: 'switch',
        page_type: 'membership',
        page_section: 'membership',
      });
      prePurchaseConfirmationModal.onOpen();
    } else {
      membership_plan_selected({
        entity_id: selectedMembershipTypeId,
        entity_type: 'membership_plan',
        membership_type_id: selectedMembershipTypeId,
        membership_plan_name: membershipTypeName,
        payment_item_type: 'membership',
        membership_change_type: 'new',
        page_type: 'membership',
        page_section: 'membership',
      });
      redirectToCheckout(selectedMembershipTypeId);
    }
  };

  return (
    <>
      <Stack w="full" h="20" bg="white" align="center">
        <HStack
          maxW="container.md"
          mx="auto"
          p="4"
          justify="space-between"
          w="full"
          h="20"
        >
          <Stack>
            <Text
              textStyle="xlBodyStrong"
              color={selectedMembershipTypeId ? 'gray.700' : 'gray.400'}
            >
              {membershipTypeName}
            </Text>

            <Text as={HStack} spacing="1">
              <CurrencyDisplay
                value={annualPrice}
                textStyle="detailStrong"
                color={selectedMembershipTypeId ? 'gray.600' : 'gray.300'}
              />

              {isMembershipV2Enabled ? (
                <>
                  <Text
                    textStyle="detailStrong"
                    color={selectedMembershipTypeId ? 'gray.600' : 'gray.300'}
                  >
                    {membershipData?.membershipType.membershipType
                      .validityMonths === -1
                      ? 'LifeTime'
                      : `of ${membershipData?.membershipType.membershipType.validityMonths} Month(s)`}
                  </Text>
                </>
              ) : (
                <>
                  <Text
                    textStyle="detailStrong"
                    color={selectedMembershipTypeId ? 'gray.600' : 'gray.300'}
                  >
                    /YEAR
                  </Text>

                  {isGreaterThanZero(monthlyPrice) && (
                    <Text
                      textStyle="detailStrong"
                      color={selectedMembershipTypeId ? 'gray.600' : 'gray.300'}
                    >
                      OR 12 PAYMENTS OF <CurrencyDisplay value={monthlyPrice} />
                    </Text>
                  )}
                </>
              )}
            </Text>
          </Stack>

          <Button
            onClick={handleSubmit}
            isLoading={isUserDataLoading}
            isDisabled={isMembershipLoading || !selectedMembershipTypeId}
          >
            Choose Membership
          </Button>
        </HStack>
      </Stack>

      <ConfirmDialog
        isOpen={duplicateMembershipModal.isOpen}
        onClose={duplicateMembershipModal.onClose}
        title={`You already have an active ${membershipTypeName} Membership`}
        body="You are only allowed to switch to a different membership than your current one. Try selecting any other membership."
        confirmButtonText="Got it"
        onConfirm={null}
        shouldHideCancelButton
      />

      <PrePurchaseConfirmationModal
        user={userData?.currentUser ?? undefined}
        isOpen={prePurchaseConfirmationModal.isOpen}
        onClose={prePurchaseConfirmationModal.onClose}
        membershipTypeId={selectedMembershipTypeId}
        isFutureMembership={isFutureMembership}
      />
    </>
  );
};
